import {getMonthHeaderWithYearForDate} from "../insight/months"
import InsightDataHelper from "../insight/insight-data-helper"
import React from "react"
import {ChartScopeSettings, isMonthChartRange, MonthChartRange} from "../insight/chart-scope-settings/chart-scope-settings"
import {Category} from "../../../domain/category"
import {CategoriesBarChart} from "../insight/categories-bar-chart/categories-bar-chart"
import {CategoriesD3Chart} from "../insight/categories-d3-chart/categories-d3-chart"
import {Heading} from "../../../components-shared/heading/heading"
import {formatAmount, formatImpact} from "../transactions/transaction-amount/transaction-amount"
import {ChartScope} from "../insight/chart-scope-settings/chart-scope"
import {SelectedCategoryPill} from "../insight/selected-item-pill/selected-category-pill"
import styles from "./category-chart.module.scss"

interface Co2OrSpendingPieChartProps {
  categoryId?: number
  spendingData: InsightDataHelper
  showCo2: boolean
  chartScopeSettings: ChartScopeSettings
  clickCategory: (category: Category) => void
}

export const Co2OrSpendingBarChart: React.FC<Co2OrSpendingPieChartProps> = ({ categoryId, spendingData, showCo2, chartScopeSettings, clickCategory }) => {
  const pieCategories = spendingData.applicableCategories.filter(c => c.parentId === categoryId && !c.isIncome)

  const topCategory = categoryId == null ? null : spendingData.applicableCategories.find(c => c.id === categoryId)
  const dataPoints = getDataPoints(pieCategories, showCo2, spendingData, chartScopeSettings)
  const title = getCategoryChartTitle(dataPoints, showCo2, topCategory, spendingData, chartScopeSettings)

  function getTooltipLabel(dataLabel: string, dataPoints: number[], value: number) {
    let totalSpent = dataPoints.reduce((total, amount) => total + amount)
    let percentage = value / totalSpent * 100
    let formattedValue = Math.round(Math.abs(value)).toLocaleString()
    if (showCo2) formattedValue += " kg CO2"
    else formattedValue = "€ " + formattedValue
    let formattedPercentage = Math.round(Math.abs(percentage)).toLocaleString()
    return `${dataLabel}: ${formattedValue} (${formattedPercentage}%)`
  }

  return CategoriesBarChart({ pieCategories, dataPoints, title, getTooltipLabel, clickCategory })
}

interface D3SpendingBarChartProps {
  categoryId?: number
  spendingData: InsightDataHelper
  showCo2: boolean
  chartScopeSettings: ChartScopeSettings
  setChartScopeSettings: (chartScopeSettings: ChartScopeSettings) => void
  clickCategory: (category: Category) => void
  clickRemoveCategory: () => void
}

export const D3SpendingBarChart: React.FC<D3SpendingBarChartProps> = ({ categoryId, spendingData, showCo2, chartScopeSettings, setChartScopeSettings, clickCategory, clickRemoveCategory }) => {
  const categories = spendingData.applicableCategories.filter(c => c.parentId === categoryId && !c.isIncome)

  const dataPoints = getDataPoints(categories, showCo2, spendingData, chartScopeSettings)

  const dataPointsWithCategory = categories.map((c, index) => {
    return {
      category: c,
      value: dataPoints[index]
    }
  })

  const topCategory = categoryId == null ? null : spendingData.applicableCategories.find(c => c.id === categoryId)
  const title = getCategoryChartTitle(dataPoints, showCo2, topCategory, spendingData, chartScopeSettings)

  return <>
    <Heading size="small" noSpacing>{title}</Heading>
    <ChartScope className={styles.ChartScope} chartScopeSettings={chartScopeSettings} setChartScopeSettings={setChartScopeSettings} spendingData={spendingData}>
      {categoryId !== undefined && <SelectedCategoryPill id={categoryId} onClick={clickRemoveCategory}/>}
    </ChartScope>
    <CategoriesD3Chart dataPointsWithCategory={dataPointsWithCategory} clickCategory={clickCategory} viewMode={showCo2 ? "co2" : "expenses"} />
  </>
}

function getDataPoints(pieCategories: Category[], showCo2: boolean, spendingData: InsightDataHelper, chartScopeSettings: ChartScopeSettings): number[] {
  return showCo2
    ? spendingData.getCo2DataPointsForSelectedScope(pieCategories, chartScopeSettings)
    : spendingData.getDataPointsForSelectedScope(pieCategories, chartScopeSettings)
}

export function getCategoryChartTitle(dataPoints: number[], showCo2: boolean, topCategory: Category | null | undefined, spendingData: InsightDataHelper, chartScopeSettings: ChartScopeSettings): string {
  if (topCategory) {
    return showCo2
      ? getSubCategoryCo2TitleForSelectedScope(dataPoints, chartScopeSettings, spendingData, topCategory)
      : getSubCategorySpendingTitleForSelectedScope(dataPoints, chartScopeSettings, spendingData, topCategory)
  }

  return showCo2
    ? getCo2TitleForSelectedScope(dataPoints, chartScopeSettings)
    : getSpendingTitleForSelectedScope(dataPoints, chartScopeSettings)
}

function getCo2TitleForSelectedScope(dataPoints: number[], chartScopeSettings: ChartScopeSettings): string {
  if (isMonthChartRange(chartScopeSettings.range)) {
    const monthHeader = getMonthHeaderWithYearForDate((chartScopeSettings.range as MonthChartRange).monthStart)
    return `CO2-uitstoot voor ${monthHeader} per categorie${chartScopeSettings.period === "year" ? " gecorrigeerd voor jaar" : ""}. Totaal: ${getFormattedTotalImpact(dataPoints)}`
  } else {
    return `Impact totaal: ${getFormattedTotalImpact(dataPoints)}`
  }
}

function getSpendingTitleForSelectedScope(dataPoints: number[], chartScopeSettings: ChartScopeSettings): string {
  if (isMonthChartRange(chartScopeSettings.range)) {
    const monthHeader = getMonthHeaderWithYearForDate((chartScopeSettings.range as MonthChartRange).monthStart)
    return `Uitgaven voor ${monthHeader} per categorie${chartScopeSettings.period === "year" ? " gecorrigeerd voor jaar" : ""}. Totaal: ${getFormattedTotalAmount(dataPoints)}`
  } else {
    return `Uitgaven totaal: ${getFormattedTotalAmount(dataPoints)}`
  }
}

function getSubCategorySpendingTitleForSelectedScope(dataPoints: number[], chartScopeSettings: ChartScopeSettings, spendingData: InsightDataHelper, topCategory: Category): string {
  if (isMonthChartRange(chartScopeSettings.range)) {
    const monthHeader = getMonthHeaderWithYearForDate((chartScopeSettings.range as MonthChartRange).monthStart)
    return `Uitgaven voor ${monthHeader} aan ${topCategory.name}${chartScopeSettings.period === "year" ? " gecorrigeerd voor jaar" : ""}. Totaal: ${getFormattedTotalAmount(dataPoints)}`
  } else {
    return `Uitgaven ${topCategory.name}: ${getFormattedTotalAmount(dataPoints)}`
  }
}

function getSubCategoryCo2TitleForSelectedScope(dataPoints: number[], chartScopeSettings: ChartScopeSettings, spendingData: InsightDataHelper, topCategory: Category): string {
  if (isMonthChartRange(chartScopeSettings.range)) {
    const monthHeader = getMonthHeaderWithYearForDate((chartScopeSettings.range as MonthChartRange).monthStart)
    return `CO2-uitstoot voor ${monthHeader} voor ${topCategory.name}${chartScopeSettings.period === "year" ? " gecorrigeerd voor jaar" : ""}. Totaal: ${getFormattedTotalImpact(dataPoints)}`
  } else {
    return `Impact ${topCategory.name}: ${getFormattedTotalImpact(dataPoints)}`
  }
}

function getFormattedTotalAmount(dataPoints: number[]): string {
  return formatAmount(Math.abs(dataPoints.reduce((total: number, amount: number) => total + amount)), 0)
}

function getFormattedTotalImpact(dataPoints: number[]): string {
  return formatImpact(Math.abs(dataPoints.reduce((total: number, amount: number) => total + amount)), 0)
}

